import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  TrendingUp, Clock, Award, Target, BarChart3,
  CheckCircle, AlertTriangle, Calendar, Activity
} from 'lucide-react';

const AgentPerformanceModal = ({ isOpen, onClose, agent, metrics }) => {
  if (!agent) return null;

  const getStatusColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Agent Performance Details"
      size="xl"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{agent.name}</h3>
            <p className="text-sm text-gray-600">{agent.department}</p>
          </div>
          {agent.performance?.performanceScore && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Performance Score</p>
              <p className={`text-2xl font-bold ${getStatusColor(agent.performance.performanceScore)}`}>
                {agent.performance.performanceScore}%
              </p>
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">Tickets Resolved</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">
              {agent.performance?.ticketsResolved || agent.ticketsResolved || 0}
            </p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-green-600" />
              <span className="text-xs font-medium text-green-900">Avg Resolution</span>
            </div>
            <p className="text-2xl font-bold text-green-900">
              {agent.performance?.avgResolutionTime || agent.avgResponseTime || '0h'}
            </p>
            <p className="text-xs text-gray-500 mt-1">hours</p>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Award className="h-5 w-5 text-purple-600" />
              <span className="text-xs font-medium text-purple-900">Satisfaction</span>
            </div>
            <p className="text-2xl font-bold text-purple-900">
              {agent.performance?.customerSatisfaction || '4.5'}
            </p>
            <p className="text-xs text-gray-500 mt-1">out of 5</p>
          </div>
          
          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-5 w-5 text-yellow-600" />
              <span className="text-xs font-medium text-yellow-900">Workload</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900">
              {agent.workload?.utilization || 0}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {agent.workload?.currentTickets || 0} / {agent.workload?.maxTickets || 15}
            </p>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Ticket Statistics
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Open Tickets</span>
                <span className="text-sm font-semibold">{agent.openTickets || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">In Progress</span>
                <span className="text-sm font-semibold">{agent.inProgress || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Pending</span>
                <span className="text-sm font-semibold">{agent.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Resolved Today</span>
                <span className="text-sm font-semibold text-green-600">{agent.resolvedToday || 0}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Award className="h-5 w-5 text-blue-600" />
              Performance Metrics
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">First Response Time</span>
                <span className="text-sm font-semibold">
                  {agent.performance?.firstResponseTime || '15m'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">SLA Compliance</span>
                <span className="text-sm font-semibold text-green-600">
                  {agent.performance?.slaCompliance || '95%'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Average Handling Time</span>
                <span className="text-sm font-semibold">
                  {agent.performance?.avgHandlingTime || '2.5h'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Quality Score</span>
                <span className="text-sm font-semibold">
                  {agent.performance?.qualityScore || '92%'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Recent Activity
          </h4>
          <div className="space-y-2">
            {metrics?.recentActivity?.map((activity, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            )) || (
              <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentPerformanceModal;

